/**
 * Which ordinary link click to record, as a pure function of what was clicked.
 *
 * The fallback behind every explicit tracker: a link to a page where a visit turns
 * into a lead -- demo, pricing, contact, sign-up, the free tools, or a mail / phone
 * link -- is recorded wherever it sits. Before this, ~40 such links (comparison pages,
 * pricing's Contact Sales, solutions, the blog sidebar, the footer, FAQ, integrations,
 * the header Pricing link) were invisible, because each needed its own onClick and
 * none had one. The DOM side is useSiteLinkTracking; this half is separate so it can
 * be tested without a browser (vitest runs in node here).
 *
 * Product-site links are not matched: useProductLinkTracking records those, with the
 * product and plan in the name.
 *
 * Labels carry a prefix for the same reason banner labels do: get_cta_attribution
 * groups by label alone, and "Pricing" or "ROI Calculator" also exist as tracked
 * buttons in the menus.
 */

export const SITE_LINK_PREFIX = 'Link · ';

/** Internal paths whose links are recorded. A path matches itself and anything under it. */
export const CONVERSION_PATHS = ['/demo', '/pricing', '/contacts', '/contact', '/sign-up', '/tools'] as const;

export type LinkRegion = 'header' | 'footer' | 'page';

export interface SiteLinkClickInput {
  href: string;
  /** The anchor's visible text. */
  text: string;
  region: LinkRegion;
  /** window.location.origin, so absolute links to this site are treated as internal. */
  origin: string;
}

export interface SiteLinkClickEvent {
  destination: string;
  label: string;
  section: 'site_link';
  location: LinkRegion;
}

function cleanLabel(text: string, fallback: string): string {
  return text.trim().replace(/\s+/g, ' ').slice(0, 120) || fallback;
}

/** The path of an internal link, or null for anything off-site. */
function internalPath(href: string, origin: string): string | null {
  if (href.startsWith('/') && !href.startsWith('//')) return href.split(/[?#]/)[0];
  try {
    const url = new URL(href);
    const self = new URL(origin);
    // hirebee.ai and www.hirebee.ai are the same site.
    const host = (h: string) => h.replace(/^www\./, '');
    if (host(url.host) !== host(self.host)) return null;
    return url.pathname;
  } catch {
    return null;
  }
}

export function isConversionPath(path: string): boolean {
  const p = path.length > 1 ? path.replace(/\/+$/, '') : path;
  return CONVERSION_PATHS.some((c) => p === c || p.startsWith(`${c}/`));
}

export function siteLinkClickEvent(input: SiteLinkClickInput): SiteLinkClickEvent | null {
  const href = input.href.trim();
  if (!href) return null;

  if (/^(mailto|tel):/i.test(href)) {
    const fallback = href.toLowerCase().startsWith('tel:') ? 'Phone' : 'Email';
    return {
      // The address itself is not a destination worth storing next to a session.
      destination: href.split(':')[0].toLowerCase() + ':',
      label: SITE_LINK_PREFIX + cleanLabel(input.text, fallback),
      section: 'site_link',
      location: input.region,
    };
  }

  const path = internalPath(href, input.origin);
  if (!path || !isConversionPath(path)) return null;
  return {
    destination: href,
    label: SITE_LINK_PREFIX + cleanLabel(input.text, path),
    section: 'site_link',
    location: input.region,
  };
}
